import { useEffect, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { UserCollection } from "@lifeat/shared/data-models/user/UserEntity";
import { SoundItem } from "@lifeat/shared/data-models/widget/Soundboard/SoundboardDataModels";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import LifeAtVolumeSlider from "@common/LifeAtVolumeSlider/LifeAtVolumeSlider";
import LifeAtVolumeToggle from "@common/LifeAtVolumeToggle/LifeAtVolumeToggle";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { useDatabaseContext } from "@contexts/DatabaseContext";
import { useGlobalAudioContext } from "@contexts/GlobalAudioContext";
import { useUserContext } from "@contexts/UserContext";
import { useDebounce } from "@hooks/useDebounce";
import { FontSize } from "@themes/Fonts";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { AmbientSoundboardPlayEvent } from "../../../../widget-workspace/AmplitudeWidgetEvents";

const AUDIO_SRC_ERROR_MSG = "Your browser does not support the audio tag.";

const SoundContainer = styled.div<{ disabled: boolean }>`
    display: flex;
    flex-direction: ${isMobile || isTablet ? "row" : "column"};
    align-items: ${isMobile || isTablet ? "center" : "flex-start"};
    justify-content: flex-start;

    opacity: ${(props) => (props.disabled ? 0.6 : 1.0)};
`;

const SoundLabel = styled.span`
    display: flex;
    gap: 8px;
    align-items: center;
    padding-bottom: 4px;
`;

const SoundImg = styled.img`
    width: 20px;
    height: 20px;
`;

const SoundName = styled.span`
    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.normal};
`;

const StyledVolumeSlider = styled(LifeAtVolumeSlider)`
    width: 75%;
`;

const PREMIUM_SOUND_CTA_TOOLTIP_LABEL = "Upgrade to access all sounds!";

interface SoundboardSoundProps {
    readonly soundInfo: SoundItem;
    readonly initialVolume: number;
    readonly shouldDisablePremiumSounds: boolean;
}

export default function SoundboardSound(props: SoundboardSoundProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const db = useDatabaseContext();
    const { user } = useUserContext();
    const { masterVolume } = useGlobalAudioContext();
    const isGlobalMuted = masterVolume === 0;
    const { soundInfo } = { ...props };
    const audioRef = useRef<HTMLAudioElement>(null);
    const [volumeSliderValue, setVolumeSliderValue] = useState(props.initialVolume);
    const debouncedVolumeSave = useDebounce(
        (value: number) =>
            db.set(
                UserCollection.at(user.uid)
                    .at("soundboardWidgetSettings")
                    .at("soundVolumes")
                    .at(props.soundInfo.soundId).node,
                value,
            ),
        1000,
    );
    const volumeInEffect = volumeSliderValue * masterVolume;

    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();

    useEffect(() => {
        if (audioRef.current) {
            if (volumeInEffect === 0) {
                audioRef.current.pause();
            } else {
                audioRef.current.volume = volumeInEffect;
                audioRef.current.play();
            }
        }
    }, [volumeSliderValue, masterVolume]);

    const logSoundboardPlayEvent = () => {
        GA4Service.logWidgetEvent(GA4Event.PLAY_SOUNDBOARD_SOUND, {
            [GA4EventProperties.soundboard_sound.key]: props.soundInfo.name,
        });
        amplitudeService.logEvent(new AmbientSoundboardPlayEvent(props.soundInfo.name));
    };

    const handleVolumeChange = (value: number) => {
        setVolumeSliderValue(value);
        debouncedVolumeSave(value);
        const becameAudible = volumeSliderValue === 0 && value > 0;
        if (becameAudible) {
            logSoundboardPlayEvent();
        }
    };

    const audioEl = (
        <audio id={soundInfo.soundId} src={soundInfo.src} loop={true} controls={false} ref={audioRef} preload="none">
            <source src={soundInfo.src} type="audio/mpeg" />
            {AUDIO_SRC_ERROR_MSG}
        </audio>
    );

    const soundInput =
        isMobile || isTablet ? (
            <LifeAtVolumeToggle
                volume={volumeSliderValue}
                setVolume={handleVolumeChange}
                disabled={props.shouldDisablePremiumSounds || isGlobalMuted}
            />
        ) : (
            <StyledVolumeSlider
                volume={volumeSliderValue}
                setVolume={handleVolumeChange}
                disabled={props.shouldDisablePremiumSounds || isGlobalMuted}
            />
        );

    const handleNonPremiumUserSoundClick = () => {
        if (props.shouldDisablePremiumSounds) {
            setUpgradeCtaContext("unlimitedSounds", UpgradeCTASource.SOUNDBOARD);
        }
    };

    return (
        <>
            <SoundContainer
                disabled={props.shouldDisablePremiumSounds || isGlobalMuted}
                data-tip={PREMIUM_SOUND_CTA_TOOLTIP_LABEL}
                data-for={"soundboard-upgrade-cta-tooltip"}
                data-tip-disable={!props.shouldDisablePremiumSounds}
                onClick={handleNonPremiumUserSoundClick}
            >
                <SoundLabel>
                    <SoundImg src={soundInfo.icon} /> <SoundName>{soundInfo.name}</SoundName>
                </SoundLabel>
                {audioEl}
                {soundInput}
            </SoundContainer>

            {props.shouldDisablePremiumSounds && <ReactTooltip id={`soundboard-upgrade-cta-tooltip`} />}
        </>
    );
}
